(() => {
  const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const normalized = value.replace(',', '.');
      const match = normalized.match(/[0-9]+(\.[0-9]+)?/);
      return match ? Number(match[0]) : null;
    }
    return null;
  };

  const parseLength = (value, unitHint) => {
    if (value === null || value === undefined || value === '') return null;
    let unit = (unitHint || '').toString().toLowerCase();
    let raw = value;
    if (typeof value === 'string') {
      raw = value.toLowerCase();
      if (!unit) {
        if (raw.includes('mm')) unit = 'mm';
        else if (raw.includes('cm')) unit = 'cm';
        else if (raw.includes('m')) unit = 'm';
      }
    }
    const num = parseNumber(raw);
    if (!num) return null;
    if (unit === 'mm') return num;
    if (unit === 'cm') return num * 10;
    if (unit === 'm') return num * 1000;
    return num;
  };

  const parseSheetSize = (value, unitHint) => {
    if (!value) return null;
    if (typeof value === 'number') return parseLength(value, unitHint);
    const raw = value.toString().toLowerCase().replace(',', '.');
    const matches = raw.match(/[0-9]+(\.[0-9]+)?/g);
    if (!matches) return null;
    const numbers = matches.map((match) => Number(match)).filter((num) => !Number.isNaN(num));
    if (!numbers.length) return null;
    const longest = Math.max(...numbers);
    return parseLength(longest, unitHint || (raw.includes('cm') ? 'cm' : raw.includes('mm') ? 'mm' : raw.includes('m') ? 'm' : ''));
  };

  const normalizeBulkTiers = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (raw.tiers && Array.isArray(raw.tiers)) return raw.tiers;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
        if (parsed.tiers) return parsed.tiers;
      } catch (error) {
        return [];
      }
    }
    return [];
  };

  const formatMoney = (amount, currency, locale, fractionDigits = 2) => {
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(safeAmount);
    } catch (error) {
      return safeAmount.toFixed(fractionDigits);
    }
  };

  const formatMoneyFromCents = (cents, currency, locale, fractionDigits = 2) =>
    formatMoney((Number(cents) || 0) / 100, currency, locale, fractionDigits);

  const initBuyerConsentCopy = () => {
    const applyShortCopy = () => {
      const consent = document.querySelector('#shopify-buyer-consent');
      if (!consent || consent.dataset.shortened === 'true') return;
      const policyButton = consent.querySelector('#shopify-subscription-policy-button');
      if (!policyButton) return;
      const prefix = 'Recurring purchase. By continuing, I agree to the ';
      const suffix = ' and authorize charges at the listed prices and schedule until I cancel.';
      consent.textContent = '';
      consent.append(document.createTextNode(prefix), policyButton, document.createTextNode(suffix));
      consent.dataset.shortened = 'true';
    };

    applyShortCopy();
    const observer = new MutationObserver(() => applyShortCopy());
    observer.observe(document.body, { childList: true, subtree: true });
  };

  class TishaProduct {
    constructor(root) {
      this.root = root;
      this.data = this.loadData();
      this.locale = document.documentElement.lang || 'en-GB';
      this.currency = root.dataset.currency || 'GBP';
      this.bulkEligible = root.dataset.bulkEligible === 'true';
      this.subscriptionPercent = parseNumber(this.data.subscription_percent || root.dataset.subscriptionPercent) || 0;
      this.showExVat = root.dataset.showExVat === 'true';
      const rawVatRate = parseNumber(root.dataset.vatRate) || 0;
      this.vatRate = rawVatRate <= 1 ? rawVatRate * 100 : rawVatRate;
      this.usePackLabels = !parseLength(root.dataset.rollLength, root.dataset.rollLengthUnit);
      this.qtyDefaultFallback = parseNumber(root.dataset.qtyDefault) || 1;
      this.qtyDefaultRolls = parseNumber(root.dataset.qtyDefaultRolls) || null;
      this.qtyBulkMultiplier =
        parseNumber(root.dataset.qtyBulkMultiplier || root.dataset.qtyBulk) || 5;
      if (this.qtyBulkMultiplier < 2) this.qtyBulkMultiplier = 5;
      this.bulkDiscountPercent = parseNumber(root.dataset.bulkDiscountPercent) || 0;
      this.bulkTiers = normalizeBulkTiers(this.data.bulk_tiers);
      if (!this.bulkTiers.length) {
        this.bulkTiers = [
          { min_qty: 5, percent: 5 },
          { min_qty: 10, percent: 8 },
          { min_qty: 25, percent: 12 },
        ];
      }

      this.variantCards = Array.from(root.querySelectorAll('[data-variant-card]'));
      this.variantSelects = root.closest('product-info')?.querySelector('variant-selects');
      this.variantIdInput = root.querySelector('.product-variant-id');
      this.quantityInput = root.querySelector('.quantity__input');
      this.quantityControls = Array.from(root.querySelectorAll('[data-qty-action]'));
      this.qtyPresetButtons = Array.from(root.querySelectorAll('[data-qty-preset]'));
      this.qtyStepperButtons = Array.from(root.querySelectorAll('[data-qty-stepper]'));
      this.qtyStepButtons = Array.from(root.querySelectorAll('[data-qty-step]'));
      this.sheetPriceValue = root.querySelector('[data-sheet-price-value]');
      this.sheetPriceWrap = root.querySelector('[data-sheet-price]');
      this.priceCurrent = root.querySelector('[data-price-current]');
      this.priceCompare = root.querySelector('[data-price-compare]');
      this.priceIncl = root.querySelector('[data-price-incl]');
      this.priceExVat = root.querySelector('[data-price-exvat]');
      this.priceSavings = root.querySelector('[data-price-savings]');
      this.bulkBadgeText = root.querySelector('[data-bulk-badge-text]');
      this.purchaseOptions = Array.from(root.querySelectorAll('[data-purchase-option]'));
      this.sellingPlanSelect = root.querySelector('[data-selling-plan-select]');
      this.sellingPlanInput = root.querySelector('[data-selling-plan-input]');
      this.stickyBar = root.querySelector('[data-sticky-bar]');
      this.stickyPrice = root.querySelector('[data-sticky-price]');
      this.stickyButton = root.querySelector('[data-sticky-submit]');
      this.stickyQty = root.querySelector('[data-sticky-qty]');
      this.tabs = Array.from(root.querySelectorAll('[data-tisha-tab]'));
      this.tabPanels = Array.from(root.querySelectorAll('[data-tisha-tab-panel]'));

      this.gallery = root.querySelector('[data-tisha-gallery]');
      this.mainImage = root.querySelector('[data-gallery-main]');
      this.galleryTrack = root.querySelector('[data-gallery-track]');
      this.gallerySlides = Array.from(root.querySelectorAll('[data-gallery-slide]'));
      this.galleryThumbs = Array.from(root.querySelectorAll('[data-gallery-thumb]'));
      this.galleryPrevMain = root.querySelector('[data-gallery-prev-main]');
      this.galleryNextMain = root.querySelector('[data-gallery-next-main]');
      this.galleryOpenTriggers = Array.from(root.querySelectorAll('[data-gallery-open]'));
      this.lightbox = root.querySelector('[data-gallery-lightbox]');
      this.lightboxImage = root.querySelector('[data-gallery-lightbox-image]');
      this.lightboxPrev = root.querySelector('[data-gallery-prev]');
      this.lightboxNext = root.querySelector('[data-gallery-next]');
      this.lightboxClose = root.querySelectorAll('[data-gallery-close]');
      const activeIndex = this.galleryThumbs.findIndex((thumb) => thumb.classList.contains('is-active'));
      this.galleryIndex = activeIndex >= 0 ? activeIndex : 0;
      this.hasSyncedInitialQty = false;
      this.galleryScrollFrame = null;
      this.cartRefreshPending = false;

      this.bind();
      this.ensureDefaultVariant();
      this.updateBulkBadge();
      this.syncUI();
    }

    loadData() {
      const script = this.root.querySelector('[data-tisha-product-data]');
      if (!script) return {};
      try {
        return JSON.parse(script.textContent || '{}');
      } catch (error) {
        return {};
      }
    }

    bind() {
      this.bindTabs();

      if (this.variantCards.length) {
        this.variantCards.forEach((card) => {
          card.addEventListener('click', () => {
            const id = Number(card.dataset.variantId);
            if (!id) return;
            const variant = this.getVariantById(id);
            if (!variant) return;
            this.applyVariantSelection(variant);
          });
          const radio = card.querySelector('input[type="radio"]');
          if (radio) {
            radio.addEventListener('change', () => {
              if (!radio.checked) return;
              const id = Number(card.dataset.variantId);
              if (!id) return;
              const variant = this.getVariantById(id);
              if (!variant) return;
              this.applyVariantSelection(variant);
            });
          }
        });
      }

      if (this.variantSelects) {
        this.variantSelects.addEventListener('change', () => {
          const variant = this.getSelectedVariant();
          if (variant && this.variantIdInput) this.variantIdInput.value = variant.id;
          this.syncUI();
        });
      }

      if (this.quantityInput) {
        this.quantityInput.addEventListener('input', () => {
          this.updatePriceDisplay();
          this.updateSheetPrice();
          this.syncQuantityPresets();
          this.updateQuantityPresetValues(this.getSelectedVariant());
          this.updateStickyQty();
        });
        this.quantityInput.addEventListener('change', () => {
          this.updatePriceDisplay();
          this.updateSheetPrice();
          this.syncQuantityPresets();
          this.updateQuantityPresetValues(this.getSelectedVariant());
          this.updateStickyQty();
        });
      }

      if (this.qtyPresetButtons.length) {
        this.qtyPresetButtons.forEach((button) => {
          button.addEventListener('click', () => {
            const value = parseNumber(button.dataset.qtyValue);
            if (!value) return;
            this.setActiveQtyCard(button);
            this.setQuantity(value);
          });
          button.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            const value = parseNumber(button.dataset.qtyValue);
            if (!value) return;
            this.setActiveQtyCard(button);
            this.setQuantity(value);
          });
        });
      }

      if (this.qtyStepperButtons.length) {
        this.qtyStepperButtons.forEach((button) => {
          button.addEventListener('click', () => {
            const action = button.dataset.qtyStepper;
            if (action === 'increase') this.adjustQuantity(1);
            if (action === 'decrease') this.adjustQuantity(-1);
          });
        });
      }

      if (this.quantityControls.length) {
        this.quantityControls.forEach((control) => {
          control.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const card = control.closest('[data-variant-card]');
            const action = control.dataset.qtyAction;
            const variantId = Number(card?.dataset.variantId || 0);
            if (variantId) {
              const variant = this.getVariantById(variantId);
              if (variant) this.applyVariantSelection(variant);
            }
            if (action === 'increase') this.adjustQuantity(1);
            if (action === 'decrease') this.adjustQuantity(-1);
          });
        });
      }

      if (this.qtyStepButtons.length) {
        this.qtyStepButtons.forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const card = button.closest('[data-qty-preset]');
            if (!card) return;
            const step = parseNumber(card.dataset.qtyValue) || 1;
            const action = button.dataset.qtyStep;
            this.setActiveQtyCard(card);
            if (action === 'increase') this.adjustQuantityByStep(step, 1);
            if (action === 'decrease') this.adjustQuantityByStep(step, -1);
          });
        });
      }

      if (this.purchaseOptions.length) {
        this.purchaseOptions.forEach((option) => {
          const input = option.querySelector('input');
          if (!input) return;
          option.addEventListener('click', () => {
            const mode = option.dataset.purchaseOption || 'one-time';
            this.setPurchaseOption(mode);
          });
          input.addEventListener('change', () => {
            const mode = option.dataset.purchaseOption || 'one-time';
            if (!input.checked) return;
            this.setPurchaseOption(mode);
          });
        });
      }

      if (this.sellingPlanSelect) {
        this.sellingPlanSelect.addEventListener('change', () => {
          this.updateSellingPlan();
          this.updateSheetPrice();
          this.updatePriceDisplay();
        });
      }

      if (this.galleryThumbs.length && this.mainImage) {
        this.galleryThumbs.forEach((thumb, index) => {
          thumb.addEventListener('click', () => {
            this.setGalleryImage(index);
          });
        });
      }

      if (this.galleryOpenTriggers.length) {
        this.galleryOpenTriggers.forEach((trigger) => {
          trigger.addEventListener('click', () => {
            if (this.gallerySlides.length) {
              const slideIndex = this.gallerySlides.indexOf(trigger);
              this.setGalleryImage(slideIndex >= 0 ? slideIndex : this.galleryIndex);
            }
            this.openLightbox();
          });
        });
      }

      if (this.galleryPrevMain) this.galleryPrevMain.addEventListener('click', () => this.stepGallery(-1));
      if (this.galleryNextMain) this.galleryNextMain.addEventListener('click', () => this.stepGallery(1));

      if (this.lightboxPrev) this.lightboxPrev.addEventListener('click', () => this.stepGallery(-1));
      if (this.lightboxNext) this.lightboxNext.addEventListener('click', () => this.stepGallery(1));
      if (this.lightboxClose.length) {
        this.lightboxClose.forEach((btn) => btn.addEventListener('click', () => this.closeLightbox()));
      }

      document.addEventListener('keydown', (event) => {
        if (!this.lightbox || this.lightbox.hasAttribute('hidden')) return;
        if (event.key === 'Escape') this.closeLightbox();
        if (event.key === 'ArrowLeft') this.stepGallery(-1);
        if (event.key === 'ArrowRight') this.stepGallery(1);
      });

      if (this.galleryTrack && this.gallerySlides.length) {
        this.galleryTrack.addEventListener('scroll', () => {
          if (this.galleryScrollFrame) return;
          this.galleryScrollFrame = window.requestAnimationFrame(() => {
            this.galleryScrollFrame = null;
            const slideWidth = this.galleryTrack.clientWidth || 1;
            const index = Math.round(this.galleryTrack.scrollLeft / slideWidth);
            this.setGalleryImage(index, { skipScroll: true });
          });
        });
      }
    }

    bindTabs() {
      if (!this.tabs.length || !this.tabPanels.length) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          this.activateTab(tab);
        });
      });

      this.root.addEventListener('keydown', (event) => {
        if (!event.target?.matches?.('[data-tisha-tab]')) return;
        const currentIndex = this.tabs.findIndex((tab) => tab === event.target);
        if (currentIndex < 0) return;

        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          const nextIndex = (currentIndex + direction + this.tabs.length) % this.tabs.length;
          this.activateTab(this.tabs[nextIndex]);
          return;
        }

        if (event.key === 'Home') {
          event.preventDefault();
          this.activateTab(this.tabs[0]);
          return;
        }

        if (event.key === 'End') {
          event.preventDefault();
          this.activateTab(this.tabs[this.tabs.length - 1]);
        }
      });

      const activeTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      this.activateTab(activeTab, { focusTab: false });
    }

    activateTab(tab, { focusTab = true } = {}) {
      if (!tab) return;
      const targetKey = tab.dataset.tishaTab;
      if (!targetKey) return;

      this.tabs.forEach((item) => {
        const isActive = item === tab;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        item.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.tabPanels.forEach((panel) => {
        const isActive = panel.dataset.tishaTabPanel === targetKey;
        panel.classList.toggle('is-active', isActive);
        if (isActive) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      if (focusTab) {
        tab.focus();
      }
    }

    ensureDefaultVariant() {
      if (!this.variantCards.length || !this.data.variants || !this.data.variants.length) return;
      if (window?.location?.search && new URLSearchParams(window.location.search).has('variant')) return;
      const current = this.getSelectedVariant();
      if (current?.available) return;
      const target = this.getDefaultVariant();
      if (target && (!current || Number(target.id) !== Number(current.id))) {
        this.applyVariantSelection(target);
      }
    }

    getDefaultVariant() {
      const variants = this.data.variants || [];
      if (!variants.length) return null;

      const availableVariants = variants.filter((variant) => variant && variant.available);
      const pool = availableVariants.length ? availableVariants : variants;

      let best = pool[0];
      let bestRolls = this.getRollsPerPack(best) || 0;
      pool.forEach((variant) => {
        const rolls = this.getRollsPerPack(variant) || 0;
        if (rolls > bestRolls) {
          best = variant;
          bestRolls = rolls;
        }
      });
      return best;
    }

    getSelectedVariant() {
      if (this.variantSelects) {
        const selects = Array.from(this.variantSelects.querySelectorAll('select'));
        if (selects.length) {
          const selectedValues = selects.map((select) => select.value);
          const match = (this.data.variants || []).find((variant) => {
            if (!variant.options) return false;
            return variant.options.every((option, index) => option === selectedValues[index]);
          });
          if (match) return match;
        }
      }

      const id = this.variantIdInput ? Number(this.variantIdInput.value) : null;
      return this.getVariantById(id) || (this.data.variants || [])[0];
    }

    getVariantById(id) {
      if (!id) return null;
      return (this.data.variants || []).find((variant) => Number(variant.id) === Number(id)) || null;
    }

    applyVariantSelection(variant) {
      if (this.variantSelects && variant.options) {
        const selects = Array.from(this.variantSelects.querySelectorAll('select'));
        selects.forEach((select, index) => {
          const nextValue = variant.options[index];
          if (!nextValue) return;
          if (select.value === nextValue) return;
          select.value = nextValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
      }
      this.syncUI();
    }

    handlePurchaseChange() {
      const selected = this.purchaseOptions.find((option) => option.querySelector('input')?.checked);
      const mode = selected?.dataset.purchaseOption || 'one-time';
      this.setPurchaseOption(mode);
    }

    setPurchaseOption(mode) {
      this.purchaseOptions.forEach((option) => {
        const input = option.querySelector('input');
        const isMatch = option.dataset.purchaseOption === mode;
        if (input) input.checked = isMatch;
        option.classList.toggle('is-active', isMatch);
      });
      this.updateSellingPlan();
      this.updatePriceDisplay();
      this.updateSheetPrice();
      this.updateQuantityPresetValues(this.getSelectedVariant());
    }

    updateSellingPlan() {
      if (!this.sellingPlanInput) return;
      const mode = this.getPurchaseMode();
      if (mode === 'subscribe' && this.sellingPlanSelect) {
        this.sellingPlanInput.value = this.sellingPlanSelect.value || '';
      } else {
        this.sellingPlanInput.value = '';
      }
      this.root.dataset.purchaseMode = mode;
    }

    getPurchaseMode() {
      const selected = this.purchaseOptions.find((option) => option.querySelector('input')?.checked);
      return selected?.dataset.purchaseOption || 'one-time';
    }

    updateBulkBadge() {
      if (!this.bulkBadgeText || !this.bulkTiers.length) return;
      const text = this.bulkTiers
        .filter((tier) => tier && tier.min_qty && tier.percent)
        .map((tier) => `${tier.min_qty}+ -${tier.percent}%`)
        .join(' / ');
      if (text) this.bulkBadgeText.textContent = text;
    }

    getRollsPerPack(variant) {
      if (!variant) return this.qtyDefaultRolls || null;
      const rollsPerPack = parseNumber(variant.rolls_per_pack);
      return rollsPerPack || this.qtyDefaultRolls || null;
    }

    getSheetsPerPack(variant) {
      if (!variant) return null;
      const directSheets = parseNumber(variant.sheets_per_pack) || parseNumber(this.root.dataset.sheetsPerPack);
      if (directSheets) return directSheets;

      let sheetsPerRoll = parseNumber(this.root.dataset.sheetsPerRoll);
      if (!sheetsPerRoll) {
        const rollLength = parseLength(this.root.dataset.rollLength, this.root.dataset.rollLengthUnit);
        let sheetLength = parseLength(this.root.dataset.sheetLength, this.root.dataset.sheetLengthUnit);
        if (!sheetLength) {
          sheetLength = parseSheetSize(this.root.dataset.sheetSize, this.root.dataset.sheetLengthUnit);
        }
        if (rollLength && sheetLength) {
          sheetsPerRoll = Math.floor(rollLength / sheetLength);
        }
      }

      let rollsPerPack = this.getRollsPerPack(variant);

      if (rollsPerPack && sheetsPerRoll) return rollsPerPack * sheetsPerRoll;
      return null;
    }

    getBulkDiscountForQty(qty) {
      if (!this.bulkEligible) return 0;
      let percent = 0;
      this.bulkTiers.forEach((tier) => {
        if (!tier || !tier.min_qty || !tier.percent) return;
        if (qty >= tier.min_qty) {
          percent = Math.max(percent, Number(tier.percent) || 0);
        }
      });
      return percent;
    }

    getDiscountPercent() {
      const mode = this.getPurchaseMode();
      if (mode === 'subscribe') return this.subscriptionPercent;
      if (!this.quantityInput) return 0;
      const qty = parseNumber(this.quantityInput.value) || 0;
      return this.getBulkDiscountForQty(qty);
    }

    updateSheetPrice() {
      if (!this.sheetPriceValue || !this.sheetPriceWrap) return;
      const variant = this.getSelectedVariant();
      const sheetsPerPack = this.getSheetsPerPack(variant);
      if (!variant || !sheetsPerPack) {
        this.sheetPriceValue.textContent = '--';
        return;
      }

      const discountPercent = this.getDiscountPercent();
      const basePrice = Number(variant.price || 0);
      const effectivePrice = basePrice * (1 - discountPercent / 100);
      const per100Currency = effectivePrice / sheetsPerPack;
      const formatted = formatMoney(per100Currency, this.currency, this.locale, 3);
      this.sheetPriceValue.textContent = formatted;
    }

    setActiveCard(variantId) {
      this.variantCards.forEach((card) => {
        const isActive = Number(card.dataset.variantId) === Number(variantId);
        card.classList.toggle('is-active', isActive);
        const radio = card.querySelector('input[type="radio"]');
        if (radio) radio.checked = isActive;
      });
    }

    syncUI() {
      const variant = this.getSelectedVariant();
      if (variant) this.setActiveCard(variant.id);
      this.syncInitialQuantity();
      this.updateQuantityPresetValues(variant);
      this.handlePurchaseChange();
      this.syncQuantityPresets();
      this.updateStickyQty();
      const mainSubmit = this.root.querySelector('.product-form__submit');
      const isAvailable = Boolean(variant?.available);

      if (this.variantIdInput) {
        this.variantIdInput.disabled = !isAvailable;
      }

      if (mainSubmit) {
        mainSubmit.disabled = !isAvailable;
        const label = mainSubmit.querySelector('span');
        if (label) {
          label.textContent = isAvailable
            ? (window.variantStrings?.addToCart || 'Add to cart')
            : (window.variantStrings?.soldOut || 'Sold out');
        }
      }

      if (this.stickyButton) {
        this.stickyButton.disabled = !isAvailable;
      }
    }

    syncInitialQuantity() {
      if (this.hasSyncedInitialQty || !this.quantityInput || !this.qtyPresetButtons.length) return;
      const defaultButton =
        this.qtyPresetButtons.find((button) => button.dataset.qtyDefault === 'true') ||
        this.qtyPresetButtons[0];
      if (!defaultButton) return;
      this.setActiveQtyCard(defaultButton);
      const targetQty = parseNumber(defaultButton.dataset.qtyValue) || parseNumber(defaultButton.dataset.qtyMultiplier) || 1;
      const currentQty = parseNumber(this.quantityInput.value) || 1;
      if (currentQty !== targetQty) this.setQuantity(targetQty);
      this.hasSyncedInitialQty = true;
    }

    updatePriceDisplay() {
      if (!this.priceCurrent) return;
      const variant = this.getSelectedVariant();
      if (!variant) return;
      const quantity = parseNumber(this.quantityInput?.value) || 1;
      const basePrice = Number(variant.price || 0);
      const compareAt = Number(variant.compare_at_price || 0);
      const baseTotal = basePrice * quantity;
      const compareTotal = compareAt > 0 ? compareAt * quantity : 0;
      const discountPercent = this.getDiscountPercent();
      let displayTotal = baseTotal;
      let comparePrice = 0;

      if (discountPercent > 0) {
        displayTotal = Math.round(baseTotal * (1 - discountPercent / 100));
        comparePrice = baseTotal;
      } else if (compareAt > basePrice) {
        comparePrice = compareTotal;
      }

      this.priceCurrent.textContent = formatMoneyFromCents(displayTotal, this.currency, this.locale, 2);
      if (this.priceCompare) {
        if (comparePrice > 0) {
          this.priceCompare.textContent = formatMoneyFromCents(comparePrice, this.currency, this.locale, 2);
          this.priceCompare.removeAttribute('hidden');
        } else {
          this.priceCompare.setAttribute('hidden', '');
        }
      }

      if (this.priceIncl) {
        this.priceIncl.textContent = `${formatMoneyFromCents(displayTotal, this.currency, this.locale, 2)} incl. VAT`;
      }

      if (this.priceExVat && this.showExVat) {
        const divisor = 1 + this.vatRate / 100;
        const exVatCents = Math.round(displayTotal / divisor);
        this.priceExVat.textContent = `${formatMoneyFromCents(exVatCents, this.currency, this.locale, 2)} ex VAT`;
      }

      if (this.priceSavings) {
        const savings = Math.max(0, comparePrice - displayTotal);
        if (savings > 0) {
          this.priceSavings.textContent = `Save ${formatMoneyFromCents(savings, this.currency, this.locale, 2)}`;
          this.priceSavings.removeAttribute('hidden');
        } else {
          this.priceSavings.setAttribute('hidden', '');
        }
      }

      if (this.stickyPrice) {
        this.stickyPrice.textContent = formatMoneyFromCents(displayTotal, this.currency, this.locale, 2);
      }

      this.updatePackCardBadges();
    }

    updateStickyQty(quantity) {
      if (!this.stickyQty) return;
      const inputQty = parseNumber(this.quantityInput?.value) || 1;
      const nextQty = Number.isFinite(quantity) ? quantity : inputQty;
      this.stickyQty.textContent = `Qty: ${nextQty}`;
    }

    handleCartUpdate(event) {
      this.updateStickyQty();
      const source = event?.source || '';
      if (!['product-form', 'tisha-product', 'quick-add'].includes(source)) return;
      this.refreshCartUi();
    }

    refreshCartUi() {
      if (this.cartRefreshPending) return;
      this.cartRefreshPending = true;

      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        this.refreshCartBubbleOnly().finally(() => {
          this.cartRefreshPending = false;
        });
        return;
      }

      const cartUrl = window.routes?.cart_url || '/cart';
      fetch(`${cartUrl}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const parsed = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceDrawer = parsed.querySelector('#CartDrawer');
          const targetDrawer = cartDrawer.querySelector('#CartDrawer');
          if (sourceDrawer && targetDrawer) {
            targetDrawer.innerHTML = sourceDrawer.innerHTML;
          }

          const sourceRoot = parsed.querySelector('cart-drawer');
          if (sourceRoot) {
            cartDrawer.classList.toggle('is-empty', sourceRoot.classList.contains('is-empty'));
          }

          if (typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.refreshCartBubbleOnly().finally(() => {
            this.cartRefreshPending = false;
          });
        });
    }

    refreshCartBubbleOnly() {
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return Promise.resolve();

      const cartUrl = window.routes?.cart_url || '/cart';
      return fetch(`${cartUrl}?section_id=cart-icon-bubble`)
        .then((response) => response.text())
        .then((responseText) => {
          const parsed = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceBubble = parsed.getElementById('cart-icon-bubble') || parsed.querySelector('.shopify-section');
          if (sourceBubble) {
            bubble.innerHTML = sourceBubble.innerHTML;
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }

    adjustQuantityByStep(step, direction) {
      if (!this.quantityInput) return;
      const min = parseNumber(this.quantityInput.min) || 1;
      const max = parseNumber(this.quantityInput.max);
      const increment = Math.max(1, Number(step) || 1);
      let current = parseNumber(this.quantityInput.value);
      if (!current || current < min) current = min;
      let next = current + increment * direction;
      if (next < min) next = min;
      if (Number.isFinite(max) && next > max) next = max;
      this.quantityInput.value = next;
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    adjustQuantity(delta) {
      if (!this.quantityInput) return;
      const step = parseNumber(this.quantityInput.step) || 1;
      const min = parseNumber(this.quantityInput.min) || 1;
      const max = parseNumber(this.quantityInput.max);
      let current = parseNumber(this.quantityInput.value);
      if (!current || current < min) current = min;
      let next = current + delta * step;
      if (next < min) next = min;
      if (Number.isFinite(max) && next > max) next = max;
      this.quantityInput.value = next;
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    setQuantity(value) {
      if (!this.quantityInput) return;
      const min = parseNumber(this.quantityInput.min) || 1;
      const max = parseNumber(this.quantityInput.max);
      let next = Math.max(min, Number(value) || min);
      if (Number.isFinite(max) && next > max) next = max;
      this.quantityInput.value = next;
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    setActiveQtyCard(card) {
      if (!card) return;
      this.activeQtyCard = card;
      this.syncQuantityPresets();
    }

    syncQuantityPresets() {
      if (!this.qtyPresetButtons.length) return;
      if (!this.activeQtyCard) {
        this.activeQtyCard =
          this.qtyPresetButtons.find(
            (button) => button.dataset.qtyDefault === 'true' || button.classList.contains('is-active')
          ) || this.qtyPresetButtons[0];
      }
      this.qtyPresetButtons.forEach((button) => {
        button.classList.toggle('is-active', button === this.activeQtyCard);
      });
    }

    updateQuantityPresetValues(variant) {
      if (!this.qtyPresetButtons.length) return;
      const baseRolls = this.getRollsPerPack(variant) || 1;
      const currentQty = parseNumber(this.quantityInput?.value) || 1;
      const mode = this.getPurchaseMode();
      const basePrice = Number(variant?.price || 0);
      const compareAt = Number(variant?.compare_at_price || 0);
      const vatDivisor = 1 + this.vatRate / 100;

      if (!this.activeQtyCard) {
        this.activeQtyCard =
          this.qtyPresetButtons.find(
            (button) => button.dataset.qtyDefault === 'true' || button.classList.contains('is-active')
          ) || this.qtyPresetButtons[0];
      }

      this.qtyPresetButtons.forEach((button, index) => {
        const rawMultiplier = parseNumber(button.dataset.qtyMultiplier);
        const isBulkCard = index !== 0;
        const quantityValue = isBulkCard
          ? rawMultiplier && rawMultiplier >= 2
            ? rawMultiplier
            : this.qtyBulkMultiplier
          : 1;
        const baseRollsValue = Math.max(1, Math.round(baseRolls * (quantityValue || 1)));
        const isActive = button === this.activeQtyCard;
        const qtyForCard = isActive ? currentQty : quantityValue;
        const rollsValue = Math.max(1, Math.round(baseRolls * qtyForCard));

        button.dataset.qtyMultiplier = quantityValue;
        button.dataset.qtyValue = quantityValue;
        button.dataset.qtyRolls = baseRollsValue;
        const label = button.querySelector('[data-qty-label]');
        if (label) label.textContent = this.packLabel(rollsValue);

        const discountPercent =
          mode === 'subscribe' ? this.subscriptionPercent : this.getBulkDiscountForQty(qtyForCard);
        const baseTotal = basePrice * qtyForCard;
        let displayTotal = baseTotal;
        let compareTotal = 0;
        if (discountPercent > 0) {
          displayTotal = Math.round(baseTotal * (1 - discountPercent / 100));
          compareTotal = baseTotal;
        } else if (compareAt > basePrice) {
          compareTotal = compareAt * qtyForCard;
        }

        const priceCurrent = button.querySelector('[data-qty-price-current]');
        if (priceCurrent) {
          priceCurrent.textContent = formatMoneyFromCents(displayTotal, this.currency, this.locale, 2);
        }
        const priceCompare = button.querySelector('[data-qty-price-compare]');
        if (priceCompare) {
          if (compareTotal > 0) {
            priceCompare.textContent = formatMoneyFromCents(compareTotal, this.currency, this.locale, 2);
            priceCompare.removeAttribute('hidden');
          } else {
            priceCompare.setAttribute('hidden', '');
          }
        }
        const priceExVat = button.querySelector('[data-qty-price-exvat]');
        if (priceExVat && this.showExVat) {
          const exVatCents = Math.round(displayTotal / vatDivisor);
          priceExVat.textContent = `${formatMoneyFromCents(exVatCents, this.currency, this.locale, 2)} ex VAT`;
        }

        const badge = button.querySelector('[data-qty-badge]');
        if (badge) {
          const percent = mode === 'subscribe' ? 0 : this.getBulkDiscountForQty(qtyForCard);
          if (percent > 0) {
            badge.textContent = `-${percent}%`;
            badge.removeAttribute('hidden');
          } else {
            badge.setAttribute('hidden', '');
          }
        }
      });
    }

    updatePackCardBadges() {
      if (!this.variantCards.length) return;
      const mode = this.getPurchaseMode();
      const qty = parseNumber(this.quantityInput?.value) || 0;
      const percent = mode === 'subscribe' ? 0 : this.getBulkDiscountForQty(qty);
      this.variantCards.forEach((card) => {
        const badge = card.querySelector('[data-pack-badge]');
        if (!badge) return;
        const isActive = card.classList.contains('is-active');
        if (percent > 0 && isActive) {
          badge.textContent = `-${percent}%`;
          badge.removeAttribute('hidden');
        } else {
          badge.setAttribute('hidden', '');
        }
      });
    }

    packLabel(value) {
      const n = Number(value) || 0;
      const unit = this.usePackLabels ? 'Pack' : 'Roll';
      return `${n} ${n === 1 ? unit : `${unit}s`}`;
    }

    forceOneTime() {
      if (!this.purchaseOptions.length) return;
      if (this.getPurchaseMode() !== 'one-time') {
        this.setPurchaseOption('one-time');
      }
    }

    setGalleryImage(index, { skipScroll = false } = {}) {
      if (!this.mainImage || !this.galleryThumbs.length) return;
      const safeIndex = (index + this.galleryThumbs.length) % this.galleryThumbs.length;
      const thumb = this.galleryThumbs[safeIndex];
      const src = thumb.dataset.full;
      if (!src) return;
      this.mainImage.src = src;
      this.mainImage.alt = thumb.dataset.alt || '';
      this.galleryThumbs.forEach((item) => item.classList.remove('is-active'));
      thumb.classList.add('is-active');
      this.galleryIndex = safeIndex;
      if (this.galleryTrack && this.gallerySlides.length && !skipScroll) {
        const slideWidth = this.galleryTrack.clientWidth || 1;
        this.galleryTrack.scrollTo({ left: slideWidth * safeIndex, behavior: 'smooth' });
      }
    }

    openLightbox() {
      if (!this.lightbox || !this.lightboxImage) return;
      this.lightbox.removeAttribute('hidden');
      document.documentElement.classList.add('tisha-lightbox-open');
      document.body.classList.add('tisha-lightbox-open');
      const current = this.galleryThumbs[this.galleryIndex];
      const src = current?.dataset.full || this.mainImage?.src;
      this.lightboxImage.src = src || '';
      this.lightboxImage.alt = current?.dataset.alt || '';
    }

    closeLightbox() {
      if (!this.lightbox) return;
      this.lightbox.setAttribute('hidden', '');
      document.documentElement.classList.remove('tisha-lightbox-open');
      document.body.classList.remove('tisha-lightbox-open');
    }

    stepGallery(step) {
      if (!this.galleryThumbs.length) return;
      this.setGalleryImage(this.galleryIndex + step);
      if (!this.lightbox || this.lightbox.hasAttribute('hidden') || !this.lightboxImage) return;
      const current = this.galleryThumbs[this.galleryIndex];
      this.lightboxImage.src = current?.dataset.full || '';
      this.lightboxImage.alt = current?.dataset.alt || '';
    }
  }

  const tishaInstances = new WeakMap();
  let cartUpdateSubscribed = false;

  const subscribeToCartUpdates = () => {
    if (cartUpdateSubscribed) return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    cartUpdateSubscribed = true;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      document.querySelectorAll('[data-tisha-product]').forEach((root) => {
        const instance = tishaInstances.get(root);
        if (instance) instance.handleCartUpdate(event);
      });
    });

    subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
      document.querySelectorAll('[data-tisha-product]').forEach((root) => {
        const instance = tishaInstances.get(root);
        if (instance) instance.updateStickyQty();
      });
    });
  };

  const bindTishaAjaxAddToCart = (scope = document) => {
    scope.querySelectorAll('[data-tisha-product] form[data-type="add-to-cart-form"]').forEach((form) => {
      if (form.dataset.tishaAjaxAtc === '1') return;
      form.dataset.tishaAjaxAtc = '1';

      form.addEventListener(
        'submit',
        async (event) => {
          // Force reliable behavior for this custom PDP:
          // add item via AJAX and redirect to /cart.
          event.preventDefault();
          event.stopImmediatePropagation();
          if (form.dataset.tishaSubmitting === '1') return;

          const submitButton = form.querySelector('[type="submit"]');
          const spinner = submitButton?.querySelector('.loading__spinner') || form.querySelector('.loading__spinner');
          const errorWrapper = form.querySelector('.product-form__error-message-wrapper');
          const errorEl = form.querySelector('.product-form__error-message');

          const setError = (message = '') => {
            if (!errorWrapper || !errorEl) return;
            if (message) {
              errorEl.textContent = message;
              errorWrapper.hidden = false;
            } else {
              errorEl.textContent = '';
              errorWrapper.hidden = true;
            }
          };

          const setLoading = (state) => {
            if (!submitButton) return;
            if (state) {
              submitButton.setAttribute('aria-disabled', 'true');
              submitButton.classList.add('loading');
              spinner?.classList.remove('hidden');
            } else {
              submitButton.removeAttribute('aria-disabled');
              submitButton.classList.remove('loading');
              spinner?.classList.add('hidden');
            }
          };

          const getFetchConfig = () => {
            if (typeof fetchConfig === 'function') return fetchConfig('javascript');
            return {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
            };
          };

          setError('');
          setLoading(true);
          form.dataset.tishaSubmitting = '1';

          try {
            const variantId = form.querySelector('[name="id"]')?.value;
            const baseUrl = window.routes?.cart_add_url || '/cart/add';
            const addUrl = baseUrl.endsWith('.js') ? baseUrl : `${baseUrl}.js`;
            const config = getFetchConfig();
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete config.headers['Content-Type'];
            config.body = new FormData(form);

            const response = await fetch(addUrl, config);
            let data = {};
            try {
              data = await response.json();
            } catch (error) {
              data = {};
            }

            if (data.status) {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'tisha-product',
                  productVariantId: variantId,
                  errors: data.errors || data.description,
                  message: data.message,
                });
              }
              setError(data.description || data.message || 'Could not add to cart');
              return;
            }

            document.dispatchEvent(new CustomEvent('tisha:cart:added'));
          } catch (error) {
            console.error(error);
            setError('Could not add to cart. Please try again.');
          } finally {
            form.dataset.tishaSubmitting = '0';
            setLoading(false);
          }
        },
        true
      );
    });
  };

  const initTishaProducts = (scope = document) => {
    scope.querySelectorAll('[data-tisha-product]').forEach((root) => {
      if (root.dataset.tishaInitialized === 'true') return;
      root.dataset.tishaInitialized = 'true';
      const instance = new TishaProduct(root);
      tishaInstances.set(root, instance);
    });
    // Custom PDP mode: submit via AJAX and redirect to cart.
    bindTishaAjaxAddToCart(scope);
    subscribeToCartUpdates();
  };

  document.addEventListener('DOMContentLoaded', () => {
    initBuyerConsentCopy();
    initTishaProducts(document);
  });

  document.addEventListener('product-info:loaded', (event) => {
    initTishaProducts(event.target);
  });
})();
